// SettingCheckboxRow — ラベル + 説明 + ON/OFF チェックボックスの 1 行
// (custom-cursor-ux フォローアップ 2026-09-19、staging 確認フィードバック
// 「スイッチが設定の文言と離れていて分かりづらい、チェックボックスに戻したうえで
// 文字を目立たせて」)。
//
// 経緯: 元は `PenSettingsPanel` / `CursorEditorPanel` それぞれの小さなチェックボックス
// だった → 発見性のためスイッチ (トグル) 式に格上げ (`SettingSwitchRow`) →
// staging で「右端のスイッチが文言と離れて分かりづらい」と再フィードバックを受け、
// **チェックボックスに戻しつつラベル文字を強調**する本 component に置き換えた
// (= このファイルは `SettingSwitchRow.tsx` からのリネーム、見た目のみ変更)。
//
// レイアウト: `<label>` 1 つでチェックボックス + ラベル文字を横並びに包み、行全体の
// クリックでトグルできるようにする (label 内に checkbox があれば標準のブラウザ挙動で
// 実現できる、追加の JS 不要)。説明文はラベル文字の下、チェックボックス幅 + gap 分
// インデントしてラベル文字の開始位置に揃える。
//
// 強調: ラベル文字は太字 + 他の設定項目 (11〜12px) より 1 段大きい 13px。ON のときは
// 既存のアクセント色 (`--color-accent`、この app の唯一のアクセント表現) に、OFF は
// 通常のテキスト色に切り替える。チェックボックス自体も `accent-color` でアクセントに
// 揃える。
//
// 可視のラベル文字 (`aria-hidden`) とは別に `<input>` へ直接 `aria-label` を渡している
// (= native `<label>` 包含によるアクセシブルネームだと、同じ `<label>` 内の説明文まで
// 連結されてしまい `getByRole('checkbox', { name: label })` の完全一致が壊れる。
// `aria-label` は host language の label 関連付けより優先されるため、説明文を無視して
// ラベル文字だけを正しい名前にできる)。
//
// **hooks を持たない pure function** (= `PenSettingsPanel.test.ts` が
// `PenSettingsPanelView` を React レンダラを介さず直接呼んで tree を walk するため、
// ここに useId 等を持ち込むと「Invalid hook call」になる。StabilizerSelect 等の
// 既存の内側 select component と同じ制約)。
//
// `CursorEditorPanel` (プレビュー直下) と `PenSettingsPanel` (ブラシカーソル項の
// 直下) の 2 箇所で全く同じ見た目を共有する。

export interface SettingCheckboxRowProps {
  /** 見出しラベル (= チェックボックスの `aria-label` も兼ねる)。 */
  readonly label: string;
  /** ラベル下の 1 行説明 (省略可)。 */
  readonly description?: string;
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
}

export const SettingCheckboxRow = ({
  label,
  description,
  checked,
  onChange,
}: SettingCheckboxRowProps): React.ReactNode => (
  <label className="flex cursor-pointer flex-col gap-0.5">
    <span className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
        className="h-4 w-4 shrink-0 accent-[var(--color-accent)]"
      />
      <span
        aria-hidden="true"
        className={`text-[13px] font-bold ${
          checked ? 'text-[var(--color-accent)]' : 'text-[var(--color-text)]'
        }`}
      >
        {label}
      </span>
    </span>
    {description !== undefined && (
      <span className="pl-6 text-[10px] text-[var(--color-text-muted)]">{description}</span>
    )}
  </label>
);
